import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface ContactMessage {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  subject: string;
  message: string;
  service_type: string;
  created_at: string;
  read_at: string | null;
}

interface MessageAnalyticsProps {
  messages: ContactMessage[];
}

const COLORS = ['hsl(180, 100%, 50%)', 'hsl(120, 100%, 50%)', 'hsl(270, 100%, 70%)', 'hsl(30, 100%, 60%)', 'hsl(320, 100%, 70%)', 'hsl(60, 100%, 60%)'];

const MessageAnalytics = ({ messages }: MessageAnalyticsProps) => {
  // Get messages by day for last 7 days
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date.toISOString().split('T')[0];
  });

  const messagesByDay = last7Days.map(day => {
    const count = messages.filter(m => m.created_at.startsWith(day)).length;
    const dayName = new Date(day).toLocaleDateString('en-US', { weekday: 'short' });
    return { day: dayName, count };
  });

  // Messages by service type
  const serviceTypeCount = messages.reduce((acc, msg) => {
    const type = msg.service_type || 'other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const serviceData = Object.entries(serviceTypeCount).map(([name, value]) => ({
    name: name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <Card className="glass p-6 border-glow">
        <h3 className="text-lg font-semibold mb-4 text-glow-cyber">Messages This Week</h3>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={messagesByDay}>
              <XAxis dataKey="day" stroke="hsl(180, 50%, 70%)" fontSize={12} />
              <YAxis stroke="hsl(180, 50%, 70%)" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(220, 50%, 6%)', 
                  border: '1px solid hsl(220, 30%, 20%)',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: 'hsl(180, 100%, 95%)' }}
              />
              <Bar dataKey="count" fill="hsl(180, 100%, 50%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="glass p-6 border-glow">
        <h3 className="text-lg font-semibold mb-4 text-glow-cyber">By Service Type</h3>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={serviceData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {serviceData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(220, 50%, 6%)', 
                  border: '1px solid hsl(220, 30%, 20%)',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default MessageAnalytics;
